import React, { FC } from 'react';
import { Link } from 'react-router-dom';

import { useCookies } from '../../context/cookies.store';
import { TermsRoute } from '../../types/page.types';
import { AppFormattedMessage } from '../app-formatted-message/index';
import Button from '../button/index';
import { CloseModalButton } from '../close-modal-button/index';
import { Sheet, SheetContent } from '../sheet/index';
import { Switch } from '../switch/index';

export type CookieSettingsModalProps = {
  handleCloseModal: () => void;
  handleOpenCookieModal: () => void;
  isOpenModal: boolean;
};

export const CookieSettingsModal: FC<CookieSettingsModalProps> = ({
  isOpenModal,
  handleCloseModal,
  handleOpenCookieModal,
}) => {
  const { cookieSettings, setCookies, toggleCookieModalOpenState } = useCookies();

  const handleAcceptAll = () => {
    setCookies({ analytics: true, essential: true, functional: true, marketing: true });
    toggleCookieModalOpenState(false);
    handleCloseModal();
  };

  const handleSaveSettings = () => {
    setCookies({
      essential: true,
      functional: cookieSettings?.functional,
      analytics: cookieSettings?.analytics,
      marketing: cookieSettings?.marketing,
    });
    toggleCookieModalOpenState(false);
    handleCloseModal();
  };

  const handleCloseWithoutChoice = () => {
    handleCloseModal();
    toggleCookieModalOpenState(false);
    if (!cookieSettings.essential) {
      handleOpenCookieModal();
    }
  };

  return (
    <Sheet onOpenChange={(open) => !open && handleCloseWithoutChoice()} open={isOpenModal}>
      <SheetContent
        className="w-full max-w-[557px] border-transparent bg-transparent p-2 shadow-3xl"
        onInteractOutside={handleCloseWithoutChoice}
        side="CENTER"
      >
        <div className="flex h-full w-full flex-col gap-6 overflow-hidden rounded-[4px] bg-white p-6">
          <div className="flex flex-col gap-4">
            <div className="flex w-full items-center justify-between">
              <span className="text-xl font-semibold text-gray-700">
                <AppFormattedMessage id="COOKIE_SETTINGS" />
              </span>
              <CloseModalButton onClose={handleCloseWithoutChoice} />
            </div>
            <span className="text-xs font-[450] text-gray-500">
              <AppFormattedMessage id="COOKIE_CONSENT_POLICY" />{' '}
              <Link
                className="inline h-fit w-fit text-brand-700 underline"
                onClick={() => window.scrollTo(0, 0)}
                target="_blank"
                to={`${TermsRoute.PRIVACY_POLICY}`}
              >
                <AppFormattedMessage id="PRIVACY_POLICY" />
              </Link>
            </span>
          </div>
          <div className="flex w-full flex-col gap-2">
            <div className="rounded-[4px] bg-gray-50 p-3">
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center justify-between">
                  <span className="text-base font-semibold text-gray-700">
                    <AppFormattedMessage id="ESSENTIAL" />
                  </span>
                  <Switch checked={true} disabled />
                </div>
                <span className="text-xs text-[450] text-gray-700">
                  <AppFormattedMessage id="ADMIN_PRIVILEGES_WARNING" />
                </span>
              </div>
            </div>

            <div className="rounded-[4px] bg-gray-50 p-3">
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center justify-between">
                  <span className="text-base font-semibold text-gray-700">
                    <AppFormattedMessage id="PERSONALISED" />
                  </span>
                  <Switch
                    checked={cookieSettings.functional}
                    onChange={(state) => {
                      setCookies({ functional: state });
                    }}
                  />
                </div>
                <span className="text-xs text-[450] text-gray-700">
                  <AppFormattedMessage id="COOKIES_STORE_PREFERENCES" />
                </span>
              </div>
            </div>

            <div className="rounded-[4px] bg-gray-50 p-3">
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center justify-between">
                  <span className="text-base font-semibold text-gray-700">
                    <AppFormattedMessage id="ANALYTICS" />
                  </span>
                  <Switch
                    checked={cookieSettings.analytics}
                    onChange={(state) => {
                      setCookies({ analytics: state });
                    }}
                  />
                </div>
                <span className="text-xs text-[450] text-gray-700">
                  <AppFormattedMessage id="ANALYTICS_POLICY" />
                </span>
              </div>
            </div>

            <div className="rounded-[4px] bg-gray-50 p-3">
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center justify-between">
                  <span className="text-base font-semibold text-gray-700">
                    <AppFormattedMessage id="MARKETING" />
                  </span>
                  <Switch
                    checked={cookieSettings.marketing}
                    onChange={(state) => {
                      setCookies({ marketing: state });
                    }}
                  />
                </div>
                <span className="text-xs text-[450] text-gray-700">
                  <AppFormattedMessage id="MARKETING_POLICY" />
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <Button
              className="w-fit text-nowrap border-[1px] border-gray-300 bg-transparent px-6 py-[10px] text-sm font-[550] text-gray-700"
              onClick={handleSaveSettings}
              styleType="NONE"
              type="button"
            >
              <AppFormattedMessage id="SAVE_SETTINGS" />
            </Button>
            <Button
              className="h-full w-full text-nowrap py-[10px] text-sm font-[550] text-white"
              onClick={handleAcceptAll}
              type="button"
            >
              <AppFormattedMessage id="ACCEPT_ALL" />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
